import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, ArrowLeft, ShieldCheck } from "lucide-react";

export default function AdminLogin() {
    const { user } = useAuth();
    const [email, setEmail] = useState("admin@sostoners.pt");
    const [password, setPassword] = useState("");
    const [totpCode, setTotpCode] = useState("");
    const [needs2FA, setNeeds2FA] = useState(false);
    const [busy, setBusy] = useState(false);

    if (user) return <Navigate to="/admin" replace />;

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const { data } = await api.post("/auth/login", {
                email, password,
                totp_code: needs2FA ? totpCode : undefined,
            });
            if (data.role !== "admin") {
                toast.error("Esta conta não tem permissões de admin.");
                setBusy(false);
                return;
            }
            localStorage.setItem("sos_token", data.token);
            window.location.href = "/admin";
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (detail === "2FA_REQUIRED") {
                setNeeds2FA(true);
                toast.info("Insira o código do Google Authenticator");
                setBusy(false);
                return;
            }
            toast.error(detail || "Erro de autenticação");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="min-h-screen bg-paper flex items-center justify-center p-6 grid-bg" data-testid="admin-login-page">
            <div className="w-full max-w-md">
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 mb-4 text-sm font-mono text-ink/60 hover:text-magenta"
                    data-testid="admin-back-to-home-link"
                >
                    <ArrowLeft size={14} /> Voltar à loja
                </Link>
                <form onSubmit={submit} className="border-2 border-ink bg-white p-8 shadow-[8px_8px_0_0_#0A0A0A]">
                    <div className="text-center mb-8">
                        <div className="font-display font-black text-3xl">
                            SOS<span className="text-magenta">TONERS</span>
                        </div>
                        <div className="label-mono mt-2">Painel de Administração</div>
                    </div>

                    {!needs2FA ? (
                        <>
                            <label className="block mb-4">
                                <span className="label-mono mb-2 block">Email</span>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="input-edge"
                                    required
                                    data-testid="admin-email-input"
                                />
                            </label>
                            <label className="block mb-2">
                                <span className="label-mono mb-2 block">Password</span>
                                <input
                                    type="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="input-edge"
                                    required
                                    data-testid="admin-password-input"
                                />
                            </label>
                            <div className="text-right mb-5">
                                <Link
                                    to="/admin/recuperar-senha"
                                    className="text-xs font-mono text-ink/60 hover:text-magenta"
                                    data-testid="admin-forgot-link"
                                >
                                    Esqueceu-se da senha?
                                </Link>
                            </div>
                        </>
                    ) : (
                        <div data-testid="admin-2fa-step">
                            <div className="border-2 border-magenta bg-magenta/5 p-4 mb-5 flex items-start gap-3">
                                <ShieldCheck size={20} className="text-magenta flex-shrink-0" />
                                <div>
                                    <div className="font-bold text-sm">Autenticação 2 Factores</div>
                                    <p className="text-xs text-ink/70 mt-1">
                                        Insira o código de 6 dígitos do Google Authenticator.
                                    </p>
                                </div>
                            </div>
                            <label className="block mb-5">
                                <span className="label-mono mb-2 block">Código TOTP (6 dígitos)</span>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    pattern="[0-9]{6}"
                                    maxLength={6}
                                    value={totpCode}
                                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                                    className="input-edge text-2xl font-mono tracking-widest text-center"
                                    required
                                    autoFocus
                                    data-testid="admin-totp-input"
                                />
                            </label>
                        </div>
                    )}
                    <button
                        type="submit"
                        disabled={busy}
                        className="btn-primary w-full disabled:opacity-50"
                        data-testid="admin-login-btn"
                    >
                        {busy && <Loader2 className="animate-spin" size={16} />}
                        {busy ? "A entrar…" : needs2FA ? "Verificar código" : "Entrar"}
                    </button>
                </form>
            </div>
        </div>
    );
}
